import React, { Suspense } from 'react';
import { StyledEngineProvider } from '@mui/material/styles';

import Site2Provider from './components/Site2Provider.js';
import Layout from './components/Layout.js';
import Footer from './components/Footer'; // Import the Footer component
import ErrorBoundary from './components/ErrorBoundary'; // Import ErrorBoundary
import PrivateRoute from './components/PrivateRoute';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import lazyWithReload from './utils/lazyWithReload';

// Every page is its own chunk (Sep 2026): the browser downloads only the code
// for the page being opened. The shell (layout, nav, auth guard) stays eager.
const Home = lazyWithReload(() => import('./pages/Home'));
const Login = lazyWithReload(() => import('./pages/Login'));
const Register = lazyWithReload(() => import('./components/Register'));
const LicenseeForm = lazyWithReload(() => import('./pages/LicenseeForm'));
const LicenseeList = lazyWithReload(() => import('./pages/LicenseeList'));
const TemporaryStays = lazyWithReload(() => import('./pages/TemporaryStays'));
const LicenseeServices = lazyWithReload(() => import('./pages/LicenseeServices.js'));
const GenerateDocuments = lazyWithReload(() => import('./pages/GenerateDocuments'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const TestSMS = lazyWithReload(() => import('./pages/TestSMS'));
const CheckoutProcess = lazyWithReload(() => import('./pages/CheckoutProcess'));
const MovingOutList = lazyWithReload(() => import('./pages/MovingOutList'));  
const ReportIssue = lazyWithReload(() => import('./pages/ReportIssue'));
const OfficeRequest = lazyWithReload(() => import('./pages/OfficeRequest'));
const PayRent = lazyWithReload(() => import('./pages/PayRent'));
const PaymentConfirmation = lazyWithReload(() => import('./pages/PaymentConfirmation'));
const GetDocuments = lazyWithReload(() => import('./pages/GetDocuments'));
const GiveNotice = lazyWithReload(() => import('./pages/GiveNotice'));
const SuccessPage = lazyWithReload(() => import('./pages/SuccessPage'));  
const PaymentStatusPage = lazyWithReload(() => import('./pages/PaymentStatusPage'));
const Profile = lazyWithReload(() => import('./pages/Profile'));
const AdminRentPageV2 = lazyWithReload(() => import('./pages/AdminRentPageV2'));
const FastPaymentHistoryPage = lazyWithReload(() => import('./pages/FastPaymentHistoryPage'));
const BulkRentGeneration = lazyWithReload(() => import('./pages/BulkRentGeneration'));
const ExternalPaymentMatcher = lazyWithReload(() => import('./pages/ExternalPaymentMatcher'));
const ReportIssueDetail = lazyWithReload(() => import('./pages/ReportIssueDetailPage.js'));
const VideoManagement = lazyWithReload(() => import('./pages/VideoManagement.js'));
const RegistrationSuccess = lazyWithReload(() => import('./components/RegistrationSuccess'));
const TrackLocation = lazyWithReload(() => import('./components/TrackLocation'));
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy'));
const HouseRules = lazyWithReload(() => import('./pages/HouseRules'));
const ReferenceUploader = lazyWithReload(() => import('./pages/ReferenceUploader'));
const ReferenceSuccessScreen = lazyWithReload(() => import('./pages/ReferenceSuccessScreen'));
const CheckReferences = lazyWithReload(() => import('./pages/CheckReferences'));
const ApproveReports = lazyWithReload(() => import('./pages/ApproveReports'));
const InvestmentSimulator = lazyWithReload(() => import('./pages/InvestmentSimulator.js'));
const UserManager = lazyWithReload(() => import('./pages/UserManager.js'));
const GenerateDepositLink = lazyWithReload(() => import('./pages/GenerateDepositLink.js'));
const GenerateRentLink = lazyWithReload(() => import('./pages/GenerateRentLink.js'));
const DepositPaymentTracker = lazyWithReload(() => import('./pages/DepositPaymentTracker.js')); 
const DepositRequestForm = lazyWithReload(() => import('./pages/DepositRequestForm.js'));
const WebhooksPage = lazyWithReload(() => import('./pages/WebhooksPage.js'));
const LatestProfitCSV = lazyWithReload(() => import('./pages/LatestProfitCSV.js'));
const MessageUs = lazyWithReload(() => import('./pages/MessageUs'));
const DepositReturnCalculator = lazyWithReload(() => import('./pages/DepositReturnCalculator.jsx')); 
const PayingRentProblems = lazyWithReload(() => import('./pages/PayingRentProblems.js'));
const GeneralEnquiries = lazyWithReload(() => import('./pages/GeneralEnquiries.js'));
const PortalImprovements = lazyWithReload(() => import('./pages/PortalImprovements.js'));
const EmptyBedsTracker = lazyWithReload(() => import('./components/EmptyBedsTracker'));
const InviteLicenseeForm = lazyWithReload(() => import('./components/InviteLicenseeForm.js'));
const NewLicenseeApproval = lazyWithReload(() => import('./pages/NewLicenseeApproval.js'));
const CompleteRegistration = lazyWithReload(() => import('./components/CompleteRegistration.js'));
const Blacklist = lazyWithReload(() => import('./pages/Blacklist.js'));
const LicenseeArchive = lazyWithReload(() => import('./pages/LicenseeArchive.js'));
// MovingInList removed - functionality merged into ScheduledMovesDashboard
const RequestDepositBack = lazyWithReload(() => import('./pages/RequestDepositBack.js'));
const GeneratedLinksTracker = lazyWithReload(() => import('./pages/GeneratedLinksTracker.js'));
const PortalAccountManager = lazyWithReload(() => import('./pages/PortalAccountManager.js'));
const MovePersonBetweenPlaces = lazyWithReload(() => import('./pages/MovePersonBetweenPlaces.js'));
const FirstRentDepositRequest = lazyWithReload(() => import('./pages/FirstDepositRentRequest.js'));
const SafetyBackupManager = lazyWithReload(() => import('./pages/SafetyBackupManager.js'));
const ScheduledMovesDashboard = lazyWithReload(() => import('./pages/ScheduledMovesDashboard.js'));
const ManageCategories = lazyWithReload(() => import('./pages/ManageCategories.js'));
const DiscountManager = lazyWithReload(() => import('./pages/DiscountManager.js'));
const ExternalPaymentsManager = lazyWithReload(() => import('./pages/ExternalPaymentsManager.js'));
const FixDateIssues = lazyWithReload(() => import('./pages/FixDateIssues.js'));
const PayRentADMINTEST = lazyWithReload(() => import('./pages/PayRentADMINTEST.tsx'));
const RefundPayment = lazyWithReload(() => import('./pages/RefundPayment.js'));
const RefundPaymentSearch = lazyWithReload(() => import('./pages/RefundPaymentSearch.js'));
const LockboxCodes = lazyWithReload(() => import('./pages/LockboxCodes.js'));
const MaintenanceLockboxCodes = lazyWithReload(() => import('./pages/MaintenanceLockboxCodes.js'));
const AlarmCodes = lazyWithReload(() => import('./pages/AlarmCodes.js'));
const SecurityAlarmCodes = lazyWithReload(() => import('./pages/SecurityAlarmCodes.js'));
const UnpaidRentTracker = lazyWithReload(() => import('./pages/UnpaidRentTracker.js'));
const AnalyticsDashboard = lazyWithReload(() => import('./pages/AnalyticsDashboard.js'));
const PleoAnalytics = lazyWithReload(() => import('./pages/PleoAnalyticsImproved.js')); // Using improved version
const TestingTutorial = lazyWithReload(() => import('./pages/TestingTutorial.js'));
const TrueLayerAccountAudit = lazyWithReload(() => import('./pages/TrueLayerAccountAudit.js'));
const SafetyEquipmentTracker = lazyWithReload(() => import('./pages/SafetyEquipmentTracker.js'));
const LabelingTracker = lazyWithReload(() => import('./pages/LabelingTracker.js'));
const MoveManagementDashboard = lazyWithReload(() => import('./pages/MoveManagementDashboard.js'));
const MyTickets = lazyWithReload(() => import('./pages/MyTickets.js'));
const BinsManagement = lazyWithReload(() => import('./pages/BinsManagement.js'));
const InternetManagement = lazyWithReload(() => import('./pages/InternetManagement.js'));
const EnergyManagement = lazyWithReload(() => import('./pages/EnergyManagement.js'));
const BoilerManagement = lazyWithReload(() => import('./pages/BoilerManagement.js'));
const GenerateReferenceLetter = lazyWithReload(() => import('./pages/GenerateReferenceLetter.js'));
const Suppliers = lazyWithReload(() => import('./pages/Suppliers.js'));
const MattressInventory = lazyWithReload(() => import('./pages/MattressInventory.js'));
const ShoppingList = lazyWithReload(() => import('./pages/ShoppingList.js'));
const HouseSetup = lazyWithReload(() => import('./pages/HouseSetup.js'));
const MyJobs = lazyWithReload(() => import('./pages/MyJobs.js'));
const GardenWork = lazyWithReload(() => import('./pages/GardenWork.js'));
const MaintenanceChecklist = lazyWithReload(() => import('./pages/MaintenanceChecklist.js'));
const ViewingsSchedule = lazyWithReload(() => import('./pages/ViewingsSchedule.js'));
const ParkingAdmin = lazyWithReload(() => import('./pages/parking/ParkingAdmin.js'));
const ParkingPortal = lazyWithReload(() => import('./pages/parking/ParkingPortal.js'));
const ParkingSignup = lazyWithReload(() => import('./pages/parking/ParkingSignup.js'));
const ParkingReportIssue = lazyWithReload(() => import('./pages/parking/ParkingReportIssue.js'));



// /legacy/* retired 18 Aug 2026 (the migration's "Phase 5"). Old bookmarks and
// muscle memory keep working: same path under the current design, query intact.
function LegacyRedirect() {
  const location = useLocation();
  const target =
    (location.pathname.replace(/^\/legacy/, '') || '/') + (location.search || '');
  return <Navigate to={target} replace />;
}

function PageLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );
}

function App() {
  return (
    <StyledEngineProvider injectFirst>
      <Site2Provider>
        <ErrorBoundary>
          <Layout>
            <Suspense fallback={<PageLoading />}>
            <Routes>
            <Route path="/" element={<Home />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/payment-success" element={<PaymentStatusPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/investment-simulator" element={<InvestmentSimulator />} />
            <Route path="/complete-registration" element={<CompleteRegistration />} />
            <Route path="/request-deposit-back" element={<RequestDepositBack />} />

            {/* Profile and My Tickets accessible by all authenticated users */}
            <Route element={<PrivateRoute />}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/my-tickets" element={<MyTickets />} />
            </Route>

              {/* Staff. View Licensees (/licensees) is declared ONCE, further down, in its own
                  block that also admits maintenance (Nov 2026); it must not be repeated here,
                  because the first matching route wins and this block would deny that role. */}
              <Route element={<PrivateRoute allowedRoles={['staff', 'admin', 'viewings', 'god']} />}>

              {/* Temporary stays (Oct 2026): staff / admin / viewings / god, not maintenance */}
              <Route path="/temporary-stays" element={<TemporaryStays />} />
              <Route path="/add-licensee" element={<LicenseeForm />} />
              <Route path="/generate-documents" element={<GenerateDocuments />} />
              {/* NEW: Consolidated Move Management Dashboard */}
              <Route path="/move-management" element={<MoveManagementDashboard />} />

              {/* LEGACY: Keep old routes for backward compatibility during transition */}
              <Route path="/moving-out-list" element={<MovingOutList />} />
              <Route path="/moving-in-list" element={<ScheduledMovesDashboard />} /> {/* Redirects to ScheduledMovesDashboard */}
              <Route path="/scheduled-moves-dashboard" element={<ScheduledMovesDashboard />} />

              <Route path="/licensee-archive" element={<LicenseeArchive />} />
              <Route path="/generate-deposit-link" element={<GenerateDepositLink />} />
              <Route path="/deposit-payment-tracker" element={<DepositPaymentTracker />} />
              <Route path="/deposit-return-calculator" element={<DepositReturnCalculator />} />
              <Route path="/portal-improvements" element={<PortalImprovements />} />
              <Route path="portal-account-manager" element={<PortalAccountManager />} />
              <Route path="/move-person-between-places" element={<MovePersonBetweenPlaces />} />
              <Route path="/safety-backup-manager" element={<SafetyBackupManager />} />
              <Route path="/firstPayment" element={<FirstRentDepositRequest />} />

            </Route>

            {/* Admin, Viewings, and Maintenance routes for Lockbox Codes */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'maintenance', 'god']} />}>
              <Route path="/lockbox-codes" element={<LockboxCodes />} />
              <Route path="/maintenance-lockbox-codes" element={<MaintenanceLockboxCodes />} />
              <Route path="/alarm-codes" element={<AlarmCodes />} />
            </Route>

            {/* Admin, Viewings, and Maintenance routes for Bins, Internet, Energy, Boiler, Suppliers, and Shopping List */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'maintenance', 'god']} />}>
              <Route path="/bins-management" element={<BinsManagement />} />
              <Route path="/internet-management" element={<InternetManagement />} />
              <Route path="/energy-management" element={<EnergyManagement />} />
              <Route path="/boiler-management" element={<BoilerManagement />} />
              <Route path="/security-alarm-codes" element={<SecurityAlarmCodes />} />
              <Route path="/suppliers" element={<Suppliers />} />
              <Route path="/mattress-inventory" element={<MattressInventory />} />
              <Route path="/shopping-list" element={<ShoppingList />} />
              <Route path="/house-setup" element={<HouseSetup />} />
              <Route path="/garden-work" element={<GardenWork />} />
            </Route>

            {/* Staff routes for Safety Equipment and Labeling */}
            <Route element={<PrivateRoute allowedRoles={['staff', 'admin', 'viewings', 'maintenance', 'god']} />}>
              <Route path="/safety-equipment-tracker" element={<SafetyEquipmentTracker />} />
              <Route path="/labeling-tracker" element={<LabelingTracker />} />
              {/* Personal work list — every staff role gets one */}
              <Route path="/my-jobs" element={<MyJobs />} />
              {/* The team's shared work plan (plan §8) */}
              <Route path="/maintenance-checklist" element={<MaintenanceChecklist />} />
              {/* Office Queue — moved here Sep 2026 so maintenance can view/filter
                  office tickets (Leonardo); page reads the same maintenance collection
                  the Dashboard already grants them */}
              <Route path="/general-enquiries" element={<GeneralEnquiries />} />
            </Route>


            {/* Admin, Viewings, and Staff routes for Licensees, Add Licensee, and Moving Out List.
                Maintenance joins for View Licensees ONLY (Nov 2026, Leonardo: the "viewing team
                spreadsheet" is the licensee roster); read-only via isLicenseeListReadOnly(). */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'staff', 'maintenance', 'god']} />}>
              <Route path="/licensees" element={<LicenseeList />} />
            </Route>
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'staff', 'god']} />}>
              <Route path="/add-licensee" element={<LicenseeForm />} />
              <Route path="/moving-out-list" element={<MovingOutList />} />
              <Route path="/generate-rent-link" element={<GenerateRentLink />} />
              <Route path="/generate-documents" element={<GenerateDocuments />} />
              <Route path="/generate-reference-letter" element={<GenerateReferenceLetter />} />
              <Route path="/track/:issueId" element={<TrackLocation />} />
              <Route path="/check-references" element={<CheckReferences />} />
              <Route path="/generate-deposit-link" element={<GenerateDepositLink />} />
              <Route path="/deposit-payment-tracker" element={<DepositPaymentTracker />} />
              <Route path="/generate-rent-link" element={<GenerateRentLink />} />
              <Route path='/invite-licensee' element={<InviteLicenseeForm />} />
              <Route path='/new-licensee-approval' element={<NewLicenseeApproval />} />
              <Route path="/deposit-return-calculator" element={<DepositReturnCalculator />} />
              <Route path="/portal-improvements" element={<PortalImprovements />} />
              <Route path="/generated-links-tracker" element={<GeneratedLinksTracker />} />
              <Route path="/safety-backup-manager" element={<SafetyBackupManager />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={['user']} />}>
              <Route path="/licensee-services" element={<LicenseeServices />} />
            </Route>

            {/* Licensee (user) routes */}
            {/* viewings added Aug 2026 (Leonardo): the team submits reports for
                issues they spot during house visits, same flow as maintenance. */}
            <Route element={<PrivateRoute allowedRoles={['user','admin','maintenance','viewings','god']} />}>
              <Route path="/report-issue" element={<ReportIssue />} />
              <Route path="/report/:category" element={<ReportIssueDetail />} />
              <Route path="/office-request/:category" element={<OfficeRequest />} />
              <Route path="/pay-rent" element={<PayRent />} />
              <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
              <Route path="/get-documents" element={<GetDocuments />} />
              <Route path="/give-notice" element={<GiveNotice />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/track/:issueId" element={<TrackLocation />} />
              <Route path="/house-rules" element={<HouseRules />} />
              <Route path="/deposit-request" element={<DepositRequestForm />} />
              <Route path="/message-us" element={<MessageUs />} />
            </Route>

            {/* Dashboard route for Maintenance, Admin, and Viewings roles */}
            <Route element={<PrivateRoute allowedRoles={['maintenance', 'admin', 'viewings', 'god']} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/test-sms" element={<TestSMS />} />
              <Route path="/checkout-process" element={<CheckoutProcess />} />
              <Route path="/video-management" element={<VideoManagement />} />
              <Route path="/approve-reports" element={<ApproveReports />} />
              <Route path="/manage-categories" element={<ManageCategories />} />
              <Route path="/safety-backup-manager" element={<SafetyBackupManager />} />
            </Route>

            {/* Admin Rent Page (Only for admins) */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'god']} />}>
              <Route path="/admin-rent-v2" element={<AdminRentPageV2 />} />
              <Route path="/unpaid-rent-tracker" element={<UnpaidRentTracker />} />
              <Route path="/payment-history" element={<FastPaymentHistoryPage />} />
              <Route path="/bulk-rent-generation" element={<BulkRentGeneration />} />
              <Route path="/external-payment-matcher" element={<ExternalPaymentMatcher />} />
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="/empty-beds-tracker" element={<EmptyBedsTracker />} />
              <Route path="/usermanager" element={<UserManager />} />
              <Route path="/webhooks-logs" element={<WebhooksPage />} />
              <Route path="/track/:issueId" element={<TrackLocation />} />
              <Route path="/paying-rent-problems" element={<PayingRentProblems />} />
              <Route path="/discount-manager" element={<DiscountManager />} />
              <Route path="/pay-rent-admin-test" element={<PayRentADMINTEST />} />
              <Route path="/refund-payment" element={<RefundPayment />} />
              <Route path="/refund-payment-search" element={<RefundPaymentSearch />} />
              <Route path="/admin/testing-tutorial" element={<TestingTutorial />} />
            </Route>


            <Route element={<PrivateRoute allowedRoles={['god']} />}>
              <Route path="/latest-profit-csv" element={<LatestProfitCSV />} />
              <Route path="/external-payments" element={<ExternalPaymentsManager />} />
              {/* Pleo Analytics - God Only - NO NAVBAR LINK */}
              <Route path="/pleo-analytics" element={<PleoAnalytics />} />
              {/* TrueLayer Account Audit - God Only - NO NAVBAR LINK */}
              <Route path="/truelayer-audit" element={<TrueLayerAccountAudit />} />
              {/* Testing Tutorial - Admin Only - NO NAVBAR LINK */}
              {/* Parking Management - God Only (income/occupancy hidden from other staff) */}
              <Route path="/parking-admin" element={<ParkingAdmin />} />
            </Route>

            {/* Parking customer portal (god included for testing) */}
            <Route element={<PrivateRoute allowedRoles={['parking', 'god']} />}>
              <Route path="/parking" element={<ParkingPortal />} />
              <Route path="/parking/report-issue" element={<ParkingReportIssue />} />
            </Route>

            {/* Parking sign-up — public, reached via invite-token email link */}
            <Route path="/parking/signup" element={<ParkingSignup />} />

            {/* Blacklist - admin, viewings, god, plus the two ISOLATED roles used by
                House Share Solutions staff: 'blacklist' (view-only) and
                'blacklist_editor' (view + add/edit). Those two reach nothing else —
                enforced in firestore.rules / storage.rules, not just here.
                See docs/core/BLACKLIST_ONLY_ROLES.md. */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'blacklist', 'blacklist_editor', 'god']} />}>
              <Route path="/blacklist" element={<Blacklist />} />
            </Route>

            {/* Viewings Schedule — admin + viewings + god, and since Oct 2026 maintenance
                (Leonardo asked for the viewings schedule in the portal instead of
                asking the viewings team for their sheet; David: "give full access").
                Firestore WRITE for maintenance comes from isViewingsScheduleUser() in
                firestore.rules, which must be deployed before this route is useful
                beyond reading (docs/viewings-flow/VIEWINGS_FLOW.md "Access"). */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'viewings', 'maintenance', 'god']} />}>
              <Route path="/viewings-schedule" element={<ViewingsSchedule />} />
            </Route>

            {/* Fix dates route for admin */}
            <Route element={<PrivateRoute allowedRoles={['admin', 'god']} />}>
              <Route path="/fix-dates" element={<FixDateIssues />} />
            </Route>

            {/* ==================================================================== */}
            {/* LEGACY UI RETIRED (18 Aug 2026) — the /legacy/* mirror of the old   */}
            {/* top-NavBar design is gone; every page lives at its canonical path   */}
            {/* under the current design. Permanent redirect below keeps old        */}
            {/* bookmarks working. NavBar.js deleted the same day.                  */}
            {/* ==================================================================== */}
            <Route path="/legacy" element={<LegacyRedirect />} />
            <Route path="/legacy/*" element={<LegacyRedirect />} />
          </Routes>
            </Suspense>

            {/* Add the Footer here to ensure it's visible on every page */}
            <Footer />
          </Layout>
        </ErrorBoundary>
      </Site2Provider>
    </StyledEngineProvider>
  );
}

export default App;
